/**
 * Reading a Google ID token, and only reading it.
 *
 * **Nothing here validates anything.** The signature, the issuer and the `aud` are checked
 * in-process by the API (`src/quest-v2-api/README.md` §5, "Auth"); a client that "verified" its own token
 * would be verifying a string it could equally have written itself. The client decodes the
 * payload for exactly two reasons: to know when the token dies (so it can renew before it
 * does, D8), and to show the player which account they are signed in as.
 */

export interface IdToken {
  /** The raw JWT. This, and nothing else, goes in the `Authorization` header. */
  credential: string;
  email: string;
  name: string | null;
  picture: string | null;
  /** Epoch milliseconds, from `exp`. Google's ID tokens live about an hour. */
  expiresAt: number;
}

/** Returns null for anything that is not a decodable JWT with an `exp` and an `email`. */
export function decodeIdToken(credential: string): IdToken | null {
  const segment = credential.split('.')[1];
  if (segment === undefined) return null;

  let claims: unknown;
  try {
    claims = decodeSegment(segment);
  } catch {
    return null;
  }
  if (typeof claims !== 'object' || claims === null) return null;

  const { exp, email, name, picture } = claims as Record<string, unknown>;
  if (typeof exp !== 'number' || typeof email !== 'string') return null;

  return {
    credential,
    email,
    name: typeof name === 'string' ? name : null,
    picture: typeof picture === 'string' ? picture : null,
    expiresAt: exp * 1000,
  };
}

export function isExpired(token: IdToken, now: number = Date.now()): boolean {
  return token.expiresAt <= now;
}

/** Milliseconds until `exp`; never negative. */
export function timeToExpiry(token: IdToken, now: number = Date.now()): number {
  return Math.max(0, token.expiresAt - now);
}

function decodeSegment(segment: string): unknown {
  const base64 = segment.replaceAll('-', '+').replaceAll('_', '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);
  // Not `atob` alone: names and email locals are UTF-8, and atob hands back one byte per
  // char. Round-tripping through TextDecoder is what keeps "Ярослав" from becoming mojibake.
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
}
